<template>
  <div :class="[{ 'nav-box': true, enabled: enabled }, props.align]">
    <ul class="node-list">
      <li>
        <div class="arrow up-arrow" @click="scrollUp" @keypress.enter="scrollUp"></div>
      </li>

      <li v-for="(screen, index) in screens" :key="`screen-${index}`">
        <div
          :class="{
            'circle-wrapper': true,
            active: index === screenIndex && targetScreenIndex === null,
          }"
          @click="scrollTo(index)"
          @keypress.enter="scrollTo(index)"
        >
          <div class="circle"></div>
        </div>
      </li>

      <li>
        <div class="arrow down-arrow" @click="scrollDown" @keypress.enter="scrollDown"></div>
      </li>
    </ul>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';

const props = defineProps({
  // Nav bar alignment
  align: {
    type: String,
    default: 'left',
  },

  // Container CSS Selector, default element is body
  containerSelector: {
    type: String,
    default: null,
  },

  screenGroupSelector: {
    type: String,
    default: null,
  },
});

const enabled = ref(true);
const container = ref(null);
const scrollHandler = ref(null);
const screens = ref([]);
const screenIndex = ref(0);
const screensHeight = ref(0);
const screensMaxWidth = ref(0);
const targetScreenIndex = ref(null);

function scrollTo(toIndex) {
  if (toIndex === screenIndex.value) {
    return;
  }

  targetScreenIndex.value = toIndex;
  screens.value[targetScreenIndex.value].scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
}

function scrollUp() {
  if (screenIndex.value > 0) {
    scrollTo(screenIndex.value - 1);
  }
}

function scrollDown() {
  if (screenIndex.value < screens.value.length - 1) {
    scrollTo(screenIndex.value + 1);
  }
}

function onScroll() {
  // Container
  const containerDimension = document.querySelector(props.containerSelector || 'body').getBoundingClientRect();
  const containerCenter = Math.round(containerDimension.height / 2);

  // Unavailable scrolling screen
  if (!screens.value[screenIndex.value]) {
    return;
  }

  // Current screen / block
  const screenDimension = screens.value[screenIndex.value].getBoundingClientRect();
  const currentScreenTop = Math.round(screenDimension.top);
  const currentScreenBottom = Math.round(screenDimension.bottom);

  const isUpOut = currentScreenBottom <= containerCenter;
  const isDownOut = currentScreenTop >= containerCenter;

  if (isUpOut) screenIndex.value += 1;
  if (isDownOut) screenIndex.value -= 1;

  // Show target screen nav button when moving by clicking nav bar
  if (screenIndex.value === targetScreenIndex.value) {
    targetScreenIndex.value = null;
  }
}

function initScrolling() {
  const containerElement = props.containerSelector ? document.querySelector(props.containerSelector) : document;

  container.value = document.querySelector(props.containerSelector || 'body');
  screens.value = document.querySelectorAll(props.screenGroupSelector);

  screensHeight.value = 0;
  screensMaxWidth.value = 0;
  screens.value.forEach((se) => {
    screensHeight.value += se.getBoundingClientRect().height;
    screensMaxWidth.value = Math.max(se.getBoundingClientRect().width, screensMaxWidth.value);
  });

  // Screen height shorter than half container height
  enabled.value = container.value.getBoundingClientRect().height < screensHeight.value;

  if (enabled.value) {
    container.value.style.setProperty('--max-screen-width', `${screensMaxWidth.value}px`);
    scrollHandler.value = containerElement.addEventListener('scroll', onScroll);
  } else {
    containerElement.removeEventListener('scroll', scrollHandler.value);
  }
}

onMounted(() => {
  window.addEventListener('resize', initScrolling);
  initScrolling();
});
onUnmounted(() => {
  window.removeEventListener('resize', initScrolling);
});
</script>

<style scoped>
.nav-box {
  position: fixed;
  text-align: center;
  width: 3em;
  color: #ffffff;
}
.nav-box:not(.enabled) {
  display: none;
}
.left {
  left: calc(50% - var(--max-screen-width) * 0.5);
  top: 50%;
  transform: translate(-100%, -50%);
}
.right {
  right: calc(50% - var(--max-screen-width) * 0.5);
  top: 50%;
  transform: translate(100%, -50%);
}

.node-list {
  margin: 0;
  padding: 0;
}
.node-list li {
  margin: 1em auto;
}

.circle-wrapper {
  display: inline-block;

  border: solid 1px transparent;
  border-radius: 50%;
  transition: border-color 0.3s;
  cursor: pointer;
}
.circle-wrapper:hover,
.circle-wrapper.active {
  border-color: white;
}
.circle-wrapper .circle {
  width: 1.1em;
  height: 1.1em;
  border-radius: 50%;
  background: #ffffff;
  opacity: 0.4;
}
.circle-wrapper:hover .circle,
.circle-wrapper.active .circle {
  opacity: 1;
}

.arrow {
  display: inline-block;
  padding: 0.4em;
  border: solid #ffffff;
  border-width: 0 2px 2px 0;
  cursor: pointer;
}
.up-arrow {
  transform: rotate(-135deg);
  -webkit-transform: rotate(-135deg);
}
.down-arrow {
  transform: rotate(45deg);
  -webkit-transform: rotate(45deg);
}

/** Landscape mobile */
@media only screen and (orientation: landscape) and (max-height: 600px) {
  .nav-box {
    display: none;
  }
}

/** Narrow devices */
@media only screen and (max-width: 768px) {
  .nav-box {
    display: none;
  }
}
</style>
